import React, { useState } from "react";
import { usePhone } from "../phone/usePhone.js";
import PhoneSignIn from "../phone/PhoneSignIn.jsx";

/**
 * Shared top-level load-failure surface for every screen (Today, Plan,
 * NCRs, Weekly report) — used in place of the "Loading…" placeholder once
 * a screen's read has actually failed rather than merely not finished yet.
 *
 * `errorCode` is one of the api/client.js envelope codes ("sign_in_required",
 * "timeout", "network_error") or an `http_<status>` code from the server —
 * `sign_in_required` gets a "Sign in again" action (auth.login(), a fresh
 * interactive sign-in), everything else gets "Retry" (re-runs the same load
 * that just failed). Reuses the existing `.placeholder`/`.btn` styles (no
 * new design system) rather than introducing a new component per screen.
 */
/** Plain wording for a load failure: what happened and what to do; the raw code is only a small detail line. */
export function errorMessage(label, errorCode) {
  const code = String(errorCode || "");
  if (code === "timeout") return `${label} took too long to load. Select Retry; if this stays, check your connection.`;
  if (code === "network_error" || /^http_(0|502|503|504)$/.test(code)) {
    return "The cockpit could not reach the server. It tries again automatically; if this stays, select Retry.";
  }
  if (code === "http_401" || code === "http_403") return `${label} could not be opened with this account. Sign in again or ask for access.`;
  return `${label} could not be loaded. Select Retry; if this stays, note the detail below.`;
}

export default function ErrorState({ label, errorCode, onSignIn, onRetry }) {
  const phone = usePhone();
  const isSignIn = errorCode === "sign_in_required";
  const [signInMessage, setSignInMessage] = useState(null);
  // A failed or blocked sign-in must never be silent (the "Sign in again does nothing" defect).
  const handleSignIn = async () => {
    setSignInMessage(null);
    try {
      const r = await onSignIn();
      // Any outcome that is not a navigation must be visible.
      if (r === null) setSignInMessage("Sign-in is already starting. If nothing happens, reload the page.");
      else if (r && r.account) setSignInMessage("You are signed in. Reload the page to continue.");
      else setSignInMessage("Redirecting to Microsoft sign-in...");
    } catch (err) {
      setSignInMessage(`Sign-in could not start (${(err && (err.errorCode || err.message)) || "unknown error"}). Try again.`);
    }
  };
  // SignIn.dc.html: a session that has ended gets the phone sign-in screen.
  if (phone && isSignIn) return <PhoneSignIn onSignIn={onSignIn} />;
  return (
    <div className="placeholder">
      <div>{isSignIn ? `${label} needs you to sign in again.` : errorMessage(label, errorCode)}</div>
      {!isSignIn && errorCode ? <div style={{ marginTop: 8, fontSize: "0.85em", opacity: 0.7 }}>Detail: {errorCode}</div> : null}
      <button
        type="button"
        className="btn acc"
        style={{ marginTop: 16 }}
        onClick={isSignIn ? handleSignIn : onRetry}
      >
        {isSignIn ? "Sign in again" : "Retry"}
      </button>
      {signInMessage ? <div role="alert" style={{ marginTop: 8 }}>{signInMessage}</div> : null}
    </div>
  );
}
